import Header from "@/atomic/atoms/Header";
import LoadingOverlay from "@/atomic/atoms/LoadingOverlay";
import { InputMask } from "@react-input/mask";
import { PropsView } from "../Models/card";
import { useState, useEffect, useRef } from "react";
import { validateCPF, validateCNPJ } from "@/utils/functions";
import Spinner from "@/atomic/atoms/Spinner";
import Modal from "@/atomic/atoms/Modal";
import useI18n from "@/hooks/useI18n";
import { useThemeContext } from "@/contexts/ThemeContext";

const INPUT_CLASS =
  "w-full text-base text-text bg-transparent border border-[#bfbfbf] p-4 rounded-[12px] h-14 placeholder:text-text-secondary tracking-[-0.4px] outline-none focus:border-primary transition-colors";

export default function CheckoutCardView({
  register,
  handleSubmit,
  errors,
  goBack,
  isDisabled,
  errorType,
  dismissError,
  isLoading,
  setValue,
  watch,
  clearErrors,
  captchaName = "mtcaptcha-checkout-card-0",
  isCaptchaVerified = false,
  isLoadingCaptchaFlag = false,
}: PropsView) {
  const { t } = useI18n();
  const { themeName } = useThemeContext();
  const title = themeName === 'zapay' ? 'Pedágio' : t("pages:checkout:payment");
  const [documentType, setDocumentType] = useState<'CPF' | 'CNPJ' | null>(null);
  const [documentValue, setDocumentValue] = useState('');
  const [showCaptcha, setShowCaptcha] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const isForeign = watch('isForeign');

  const handleDocumentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    if (isForeign) {
      const cleanValue = value.replace(/[^a-zA-Z0-9]/g, '');
      setDocumentValue(cleanValue);

      if (inputRef.current) {
        inputRef.current.value = cleanValue;
      }

      if (setValue) {
        setValue('document', cleanValue, { shouldValidate: true });
      }
    } else {
      const numericValue = value.replace(/\D/g, '');
      setDocumentValue(numericValue);

      let formattedValue = numericValue;

      if (numericValue.length === 11) {
        const cpfWithMask = numericValue.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
        if (validateCPF(cpfWithMask)) {
          setDocumentType('CPF');
          formattedValue = cpfWithMask;
        } else {
          setDocumentType(null);
        }
      } else if (numericValue.length === 14) {
        const cnpjWithMask = numericValue.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
        if (validateCNPJ(cnpjWithMask)) {
          setDocumentType('CNPJ');
          formattedValue = cnpjWithMask;
        } else {
          setDocumentType(null);
        }
      } else {
        setDocumentType(null);
      }

      if (inputRef.current) {
        inputRef.current.value = formattedValue;
      }

      if (setValue) {
        setValue('document', formattedValue, { shouldValidate: true });
      }
    }
  };

  const handleDocumentPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData('text');

    if (isForeign) {
      const cleanValue = pastedText.replace(/[^a-zA-Z0-9]/g, '');
      setDocumentValue(cleanValue);
      if (setValue) {
        setValue('document', cleanValue, { shouldValidate: true });
      }
    } else {
      const cleanValue = pastedText.replace(/\D/g, '');

      if (cleanValue.length === 11) {
        const cpfWithMask = cleanValue.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
        if (validateCPF(cpfWithMask)) {
          setDocumentType('CPF');
          setDocumentValue(cleanValue);
          if (inputRef.current) {
            inputRef.current.value = cpfWithMask;
          }
          if (setValue) {
            setValue('document', cpfWithMask, { shouldValidate: true });
          }
        }
      } else if (cleanValue.length === 14) {
        const cnpjWithMask = cleanValue.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
        if (validateCNPJ(cnpjWithMask)) {
          setDocumentType('CNPJ');
          setDocumentValue(cleanValue);
          if (inputRef.current) {
            inputRef.current.value = cnpjWithMask;
          }
          if (setValue) {
            setValue('document', cnpjWithMask, { shouldValidate: true });
          }
        }
      }
    }
  };

  const getFormattedValue = () => {
    if (isForeign) {
      return documentValue;
    }
    if (documentType === 'CPF' && documentValue.length === 11) {
      return documentValue.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    } else if (documentType === 'CNPJ' && documentValue.length === 14) {
      return documentValue.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
    }
    return documentValue;
  };

  const handleIsForeignChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;

    if (!checked) {
      setDocumentValue('');
      setDocumentType(null);

      if (inputRef.current) {
        inputRef.current.value = '';
      }

      if (setValue) {
        setValue('document', '', { shouldValidate: false });
        setValue('isForeign', false);
      }

      if (clearErrors) {
        clearErrors('document');
      }
    } else {
      if (setValue) {
        setValue('isForeign', true);
      }
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowCaptcha(true);
    }, 100);

    return () => {
      clearTimeout(timer);
      setShowCaptcha(false);
    };
  }, [captchaName]);

  useEffect(() => {
    if (inputRef.current && documentValue.length > 0 && !isForeign) {
      const formattedValue = getFormattedValue();
      inputRef.current.value = formattedValue;
    }
  }, [documentType, documentValue, isForeign]);

  return (
    <div className="h-full flex flex-col">
      <Header
        title={title}
        hasLeftButton
        goBack={goBack}
      />

      <form
        className="flex-1 flex flex-col overflow-hidden bg-white"
        onSubmit={handleSubmit}
      >
        <main className="flex-1 overflow-y-auto px-4 pb-28">
          <div className="max-w-[480px] mx-auto">
            {!errorType && (
              <div className="flex flex-col gap-[17px]">
                <h2 className="text-[22px] font-bold text-text mt-6 tracking-[-0.2px] leading-[1.2]">
                  Informe os dados do cartão
                </h2>

                <div className="bg-[#e6e6e6] rounded-2xl h-[100px] w-full relative flex items-center overflow-hidden">
                  <div className="flex flex-col gap-1 pl-5 pr-2 z-10 max-w-[55%]">
                    <p className="text-sm font-medium text-text tracking-[-0.4px] leading-[1.3]">
                      Cartão Sem Parar Mais
                    </p>
                    <p className="text-xs text-text-secondary leading-[1.4]">
                      Economize até R$ 35,00 ao parcelar
                    </p>
                    <a
                      href="https://www.semparar.com.br/sem-parar-mais"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm font-medium text-primary tracking-[-0.4px] leading-[1.3] mt-1"
                    >
                      Pedir agora
                    </a>
                  </div>
                  <img
                    src="/svg/card-semparar.svg"
                    alt="Cartão Sem Parar"
                    className="absolute right-[-16px] -top-3 h-[140px] rotate-[16deg] pointer-events-none drop-shadow-md"
                  />
                </div>

                <div className="w-full">
                  <InputMask
                    {...register("number")}
                    name="number"
                    required
                    type="text"
                    inputMode="numeric"
                    mask="____ ____ ____ ______"
                    replacement={{ _: /\d/ }}
                    placeholder="Número do cartão de crédito"
                    className={INPUT_CLASS}
                  />
                  <div className="min-h-[1rem] mt-1">
                    {errors.number && (
                      <span className="text-error text-xs break-words">{errors.number.message as string}</span>
                    )}
                  </div>
                </div>

                <div className="w-full">
                  <input
                    {...register("name")}
                    required
                    type="text"
                    name="name"
                    placeholder="Nome impresso no cartão"
                    className={INPUT_CLASS}
                  />
                  <div className="min-h-[1rem] mt-1">
                    {errors.name && (
                      <span className="text-error text-xs break-words">{errors.name.message as string}</span>
                    )}
                  </div>
                </div>

                <div className="w-full">
                  <InputMask
                    {...register("expiration")}
                    name="expiration"
                    required
                    type="text"
                    inputMode="numeric"
                    mask="__/____"
                    replacement={{ _: /\d/ }}
                    placeholder="Validade"
                    className={INPUT_CLASS}
                  />
                  <div className="min-h-[1rem] mt-1">
                    {errors.expiration && (
                      <span className="text-error text-xs break-words">{errors.expiration.message as string}</span>
                    )}
                  </div>
                </div>

                <div className="w-full">
                  <input
                    {...register("document")}
                    ref={inputRef}
                    name="document"
                    required
                    type="text"
                    inputMode={isForeign ? "text" : "numeric"}
                    placeholder={isForeign ? t("pages:checkout:documentPlaceholderForeign") : "CPF do titular"}
                    onChange={handleDocumentChange}
                    maxLength={isForeign ? 11 : 18}
                    onPaste={handleDocumentPaste}
                    className={INPUT_CLASS}
                  />
                  <div className="min-h-[1rem] mt-1">
                    {errors.document && (
                      <span className="text-error text-xs break-words">{errors.document.message as string}</span>
                    )}
                  </div>

                  <div className="flex items-center mt-2">
                    <input
                      {...register("isForeign")}
                      type="checkbox"
                      id="isForeign"
                      className="w-5 h-5 accent-primary mr-2"
                      onChange={handleIsForeignChange}
                    />
                    <label htmlFor="isForeign" className="text-sm text-text cursor-pointer">
                      {t("pages:checkout:isForeign")}
                    </label>
                  </div>
                </div>

                {showCaptcha && (
                  <div className="w-full mb-4 flex justify-center relative">
                    <LoadingOverlay isLoading={isLoadingCaptchaFlag} bgColor="bg-white/50" />                    
                    <div
                      className="mtcaptcha"
                      id={captchaName}
                      data-callback="onCaptchaSuccess"
                    ></div>
                  </div>
                )}

              </div>
            )}
          </div>
        </main>

        <div className="fixed bottom-0 left-0 right-0 px-5 py-2.5 bg-white">
          <div className="max-w-[480px] mx-auto">
            <button
              type="submit"
              disabled={isDisabled || !isCaptchaVerified}
              className={`w-full h-[60px] rounded-theme-large text-base font-medium text-white tracking-[-0.4px] transition-opacity rounded-button ${
                isDisabled || !isCaptchaVerified
                  ? 'bg-disabled text-disabled-text cursor-not-allowed'
                  : 'bg-primary cursor-pointer'
              }`}
            >
              {t("common:continue")}
            </button>
          </div>
        </div>
      </form>

      {isLoading && <Spinner />}

      {errorType === 'charge_failed' && (
        <Modal
          title={t("pages:checkout:errors:chargeFailedTitle")}
          message={t("pages:checkout:errors:chargeFailedMessage")}
          action={handleSubmit}
          actionMessage={t("pages:checkout:errors:tryAgain")}
          actionSecond={dismissError}
          actionSecondMessage={t("common:cancel")}
          actionClose={dismissError}
        />
      )}

      {errorType === 'processing_failed' && (
        <Modal
          title={t("pages:checkout:errors:processingFailedTitle")}
          message={t("pages:checkout:errors:processingFailedMessage")}
          action={goBack}
          actionMessage={t("common:back")}
          hasCloseButton={true}
          actionClose={goBack}
        />
      )}
    </div>
  );
}
